import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Crown, MapPin, Eye, Heart, Star, ShieldCheck } from "lucide-react";
import { playElegantClick } from "@/lib/sound";

// Elegant placeholder used when a profile has no photo or the URL fails to load.
// Inline SVG (no network) — silhouette + scarlet glow + initial letter.
const placeholderFor = (name = "") => {
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 800'>
    <defs>
      <radialGradient id='g' cx='50%' cy='35%' r='65%'>
        <stop offset='0%' stop-color='#3a0a14'/>
        <stop offset='55%' stop-color='#15080a'/>
        <stop offset='100%' stop-color='#050505'/>
      </radialGradient>
      <linearGradient id='glow' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stop-color='#C8102E' stop-opacity='0.85'/>
        <stop offset='100%' stop-color='#7F1D1D' stop-opacity='0.3'/>
      </linearGradient>
    </defs>
    <rect width='600' height='800' fill='url(#g)'/>
    <circle cx='300' cy='340' r='160' fill='none' stroke='url(#glow)' stroke-width='2.5' opacity='0.75'/>
    <text x='300' y='378' text-anchor='middle' font-family='Cormorant Garamond, Georgia, serif' font-size='180' font-weight='300' fill='#F5F5F4' opacity='0.92'>${initial}</text>
    <text x='300' y='640' text-anchor='middle' font-family='Outfit, sans-serif' font-size='22' letter-spacing='6' fill='#C8102E' opacity='0.7'>PK2 SCARLETT</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

/**
 * Neon-glass MusaCard — frosted glass + crimson neon glow border, circular avatar
 * with red light ring, white typography, gentle pulse animation and a 3D tilt on
 * pointer move.
 */
export default function MusaCard({ profile, index = 0 }) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const placeholder = placeholderFor(profile.stage_name);
  const initialHero =
    profile.hero_photo ||
    (profile.photos && profile.photos[0]) ||
    placeholder;
  const [hero, setHero] = useState(initialHero);
  const onImgError = () => setHero(placeholder);

  // Stats fallbacks — directory-friendly
  const stats = [
    { icon: <Eye size={11} strokeWidth={1.6} />, value: (profile.views ?? 0).toLocaleString("es-MX"), label: t("profile.views") },
    { icon: <Heart size={11} strokeWidth={1.6} />, value: profile.rating_count ?? 0, label: t("profile.reviews") },
    {
      icon: <Star size={11} strokeWidth={1.6} fill="currentColor" />,
      value: profile.rating_avg ? Number(profile.rating_avg).toFixed(1) : "5.0",
      label: t("profile.rating"),
    },
  ];

  // 3D tilt on pointer move (subtle, max 8deg)
  const onMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) translateY(-4px)`;
  };
  const onLeave = () => {
    const el = cardRef.current;
    if (el) el.style.transform = "";
  };

  return (
    <Link
      to={`/musa/${profile.slug || profile.id}`}
      ref={cardRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-no-sound
      onClick={() => playElegantClick(((index || 0) % 12) / 12)}
      className="musa-card-neon relative block fade-up"
      style={{ animationDelay: `${Math.min(index, 12) * 70}ms` }}
      data-testid={`musa-card-${profile.id}`}
    >
      {/* Background photo */}
      <div className="musa-card-photo">
        <img
          src={hero}
          alt={profile.stage_name}
          loading="lazy"
          draggable={false}
          onError={onImgError}
        />
        <div className="musa-card-photo-mask" />
      </div>

      {/* Glass body */}
      <div className="musa-card-glass">
        {/* Avatar with red glow ring */}
        <div className="musa-card-avatar">
          <img src={hero} alt="" draggable={false} onError={onImgError} />
          {profile.verified && (
            <span className="musa-card-verified" title="Verificada">
              <ShieldCheck size={11} strokeWidth={2} />
            </span>
          )}
        </div>

        {/* Name + role */}
        <h3 className="musa-card-name">{profile.stage_name}</h3>
        <p className="musa-card-role">
          <MapPin size={10} strokeWidth={1.6} />
          {profile.city}{profile.age ? ` · ${profile.age} años` : ""}
        </p>

        {profile.display_id && (
          <p className="text-[10px] mt-1 mb-0.5 font-mono tracking-widest text-center" style={{ color: "rgba(255,255,255,0.45)", letterSpacing: "0.18em" }} data-testid={`musa-card-display-id-${profile.id}`}>
            ID · {profile.display_id}
          </p>
        )}

        {/* Stats grid */}
        <div className="musa-card-stats">
          {stats.map((s, i) => (
            <div key={i} className="musa-card-stat">
              <div className="musa-card-stat-icon">{s.icon}</div>
              <div className="musa-card-stat-value">{s.value}</div>
              <div className="musa-card-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* VIP crown */}
      {profile.is_vip && (
        <div className="musa-card-vip" data-testid={`vip-badge-${profile.id}`}>
          <Crown size={10} strokeWidth={1.8} /> VIP
        </div>
      )}
    </Link>
  );
}
